/**
 * Facade over the auto-generated `./schema.d.ts`.
 *
 * Every type the rest of the app imports as `@/api/types` is now
 * sourced from the FastAPI OpenAPI schema (`npm run gen:api`). This
 * file is a thin re-export layer so the migration is a no-op at call
 * sites and frontend-only types (enums the API doesn't ship, runtime
 * constants like `SOURCES_REQUIRING_SUPPLIER_FILE`) have a single
 * home.
 *
 * Adding a new server type:
 *   1. Update the Pydantic model in `server-py`
 *   2. `npm run gen:api` regenerates `schema.d.ts`
 *   3. Add a one-liner re-export below (or leave it implicit if the
 *      shape is only used internally — components are addressable as
 *      `components['schemas']['Foo']` from anywhere)
 *
 * The CI gate `gen:api:check` fails the build on any divergence
 * between `schema.d.ts` and the committed copy, so a rename on the
 * Python side surfaces as a tsc error after regen.
 */

import type { components } from './schema';

// ── Re-exports from the generated schema ─────────────────────────────

export type CustomerOut = components['schemas']['CustomerOut'];
export type CustomerListOut = components['schemas']['CustomerListOut'];

export type EngagementOut = components['schemas']['EngagementOut'];
export type EngagementListOut = components['schemas']['EngagementListOut'];

export type AuditRunOut = components['schemas']['AuditRunOut'];
export type AuditRunListOut = components['schemas']['AuditRunListOut'];
export type FilingOut = components['schemas']['FilingOut'];
export type FilingCreate = components['schemas']['FilingCreate'];
export type FilingPreviewOut = components['schemas']['FilingPreviewOut'];
export type MaterialityPreviewOut = components['schemas']['MaterialityPreviewOut'];
export type MaterialityPreviewPoint = components['schemas']['MaterialityPreviewPoint'];
export type OrphanItcEntriesOut = components['schemas']['OrphanItcEntriesOut'];
export type OrphanItcEntryOut = components['schemas']['OrphanItcEntryOut'];

export type FindingOut = components['schemas']['FindingOut'];
export type FindingListOut = components['schemas']['FindingListOut'];
export type FindingStatusBucketOut = components['schemas']['FindingStatusBucketOut'];
export type RunFindingsSummaryOut = components['schemas']['RunFindingsSummaryOut'];
export type RunFindingsFacetsOut = components['schemas']['RunFindingsFacetsOut'];
export type FindingStatusEventOut = components['schemas']['FindingStatusEventOut'];

export type RuleAuthorityListOut = components['schemas']['RuleAuthorityListOut'];
export type RuleAuthorityOut = components['schemas']['RuleAuthorityOut'];

export type KbAnswerOut = components['schemas']['KbAnswerOut'];
export type SuggestionOut = components['schemas']['SuggestionOut'];
export type CitationOut = components['schemas']['CitationOut'];
export type KbUsageTotalsOut = components['schemas']['KbUsageTotalsOut'];
export type KbUsageRollupOut = components['schemas']['KbUsageRollupOut'];
export type FindingStatusEventListOut = components['schemas']['FindingStatusEventListOut'];
export type FindingRecoveryEventOut = components['schemas']['FindingRecoveryEventOut'];
export type FindingRecoveryEventListOut = components['schemas']['FindingRecoveryEventListOut'];

export type MoneyOut = components['schemas']['MoneyOut'];

export type SupplierViewOut = components['schemas']['SupplierViewOut'];
export type SupplierJournalEntriesOut = components['schemas']['SupplierJournalEntriesOut'];
export type SupplierBnMapOut = components['schemas']['SupplierBnMapOut'];
export type SupplierAggregatesOut = components['schemas']['SupplierAggregatesOut'];
export type SupplierGraphOut = components['schemas']['SupplierGraphOut'];
export type SupplierGraphNodeOut = components['schemas']['SupplierGraphNodeOut'];
export type SupplierGraphEdgeOut = components['schemas']['SupplierGraphEdgeOut'];

export type JournalEntryOut = components['schemas']['JournalEntryOut'];
export type TransactionJournalEntryOut =
  components['schemas']['TransactionJournalEntryOut'];

/** Generated `RuleOut` loses type-safety on `trigger` and
 *  `recovery_formula` because Pydantic discriminated unions don't
 *  survive the JSON-schema round-trip cleanly. Override those two
 *  fields with the hand-written typed shapes (see below). */
export type RuleOut = Omit<
  components['schemas']['RuleOut'],
  'trigger' | 'recovery_formula'
> & {
  trigger: RuleTrigger;
  recovery_formula: RuleRecoveryFormula;
};
export type RuleListOut = Omit<components['schemas']['RuleListOut'], 'items'> & {
  items: RuleOut[];
};

/** Server-side the Pydantic model is named `RuleSet` (no `Out`
 *  suffix) — alias for consistency with the other resource shapes. */
export type RuleSetOut = components['schemas']['RuleSet'];
export type RuleSetListOut = components['schemas']['RuleSetListOut'];

// ── Literal-union extracts ───────────────────────────────────────────
//
// FastAPI's Pydantic `Literal[...]` types are inlined as string-union
// literals in `schema.d.ts` (not separate named types). Extract them
// here so the rest of the app can import friendly names.

export type FindingStatus = FindingOut['status'];
export type FindingCategory = FindingOut['category'];
export type FindingOutcome = FindingOut['outcome'];
export type ExpenseSource = FindingOut['expense_source'];
export type LedgerSource = FindingOut['ledger_source'];
/** Reason-code Literal — element type of `FindingOut.reason_codes`.
 *  The frozen 12-bucket taxonomy practitioners pick from when rejecting.
 *  Source of truth: `server_py/engine/reason_codes.py` (Pydantic Literal). */
export type ReasonCode = FindingOut['reason_codes'][number];
/** Approval-reason Literal — the single-select confidence basis a
 *  practitioner picks when approving. Source of truth:
 *  `server_py/engine/approval_reasons.py`. */
export type ApprovalReason = NonNullable<FindingOut['approval_reason']>;
export type AuditRunSourceType = AuditRunOut['source_type'];
export type EngagementStatus = EngagementOut['status'];
// The customer-facing billing lifecycle, distinct from the practitioner `status`.
export type PortalState = EngagementOut['portal_state'];
// `default_province` is the only ProvinceCode-shaped field — extract.
export type ProvinceCode = NonNullable<EngagementOut['default_province']>;

// ── Frontend-only types (not in the API schema) ──────────────────────

/** Frontend-derived rollup of the engagement's status into the four
 *  buckets shown on the dashboard tile. Not on the API today; derived
 *  from `engagement.status` client-side. */
export type AccountStatus = 'in_progress' | 'reviewing' | 'completed' | 'none';

/** Source-type membership test: which uploads require a separate
 *  supplier file in addition to the transactions file. Frontend
 *  constant — not in the schema; the API enforces the same rule
 *  server-side. */
export const SOURCES_REQUIRING_SUPPLIER_FILE: ReadonlySet<AuditRunSourceType> = new Set([
  'qbo',
]);

// ── Rule-card editorial types ────────────────────────────────────────
//
// The Rules wiki UI reshapes `RuleOut.trigger` / `recovery_formula`
// into typed predicates for rendering. The OpenAPI schema flattens
// these to `{[k:string]: unknown}` because the underlying Pydantic
// models use discriminated unions that don't survive the JSON-schema
// round-trip. Kept as hand-written types here.

/** Tolerance band against a literal expected value — pairs with the
 *  `not_` combinator in a trigger node to express "fires unless effective
 *  ≈ expected." Either pct or dollars may be set (or both). Numbers ship
 *  as strings (serialized Decimal) on the wire. */
export interface WithinToleranceOf {
  expected: string | number;
  tolerance_pct?: string | number | null;
  tolerance_dollars?: string | number | null;
}

/** Single predicate from the trigger DSL. Each predicate sets exactly
 *  one comparator field; the others are null/undefined on the wire. */
export interface RulePredicate {
  field: string;
  equals?: unknown;
  not_equals?: unknown;
  one_of?: unknown[] | null;
  /** Case-insensitive substring match against any of these. */
  contains_any?: string[] | null;
  gte?: string | null; // serialized Decimal
  lte?: string | null; // serialized Decimal
  within_engagement_period?: boolean | null;
  within_reclaim_window_years?: number | null;
  within_tolerance_of?: WithinToleranceOf | null;
}

/** Nested boolean combinator inside a trigger expression. Exactly one
 *  of `all_of` / `any_of` / `not_` is non-null per node — mirrors the
 *  Pydantic schema. The wire ships the python attribute name `not_`
 *  (Pydantic's serialization_alias doesn't apply inside model_dump),
 *  so we match that here. The other two are kept for symmetry — every
 *  combinator node ships all three fields with nulls in the unset slots. */
export interface NestedTrigger {
  all_of?: RuleTriggerNode[] | null;
  any_of?: RuleTriggerNode[] | null;
  not_?: RuleTriggerNode | null;
}

/** A trigger child is either a leaf predicate or a nested combinator.
 *  Distinguished structurally: a node with `field` is a predicate. */
export type RuleTriggerNode = RulePredicate | NestedTrigger;

export interface RuleTrigger {
  all_of: RuleTriggerNode[];
}

export interface RuleFactorFormula {
  op: 'factor';
  base_field: string;
  numerator: number;
  denominator: number;
}

/** Date-aware Factor Method — the rate is looked up from the engine's
 *  rate history by (jurisdiction, rate_type, as_of) instead of being a
 *  static numerator/denominator (e.g. NS HST 15%→14% on 2025-04-01). */
export interface RuleRateLookupFactorFormula {
  op: 'rate_lookup_factor';
  base_field: string;
  jurisdiction_field: string;
  as_of_field: string;
  rate_type: 'hst' | 'gst' | 'qst' | 'pst';
}

export type RuleRecoveryFormula = RuleFactorFormula | RuleRateLookupFactorFormula;

/** Supplier-drawer `source` field — practitioner-facing label for
 *  which classification layer answered. Not exposed as a named type
 *  in the schema. */
export type ClassificationSource =
  components['schemas']['SupplierClassificationOut']['source'];

// ── Schema escape hatch ──────────────────────────────────────────────
//
// Re-exported for the rare call site that needs to address a generated
// type that isn't aliased above (e.g. a nested request body type).
// Prefer adding a named export above when a type gets used in >1 place.
export type { components, paths } from './schema';
